import React from "react";
import type { Asset } from "../../model/asset.model";
import { useNavigateToTitle } from "../../hooks/useNavigateToTitle";
import { parsePrimaryTag } from "../../mappers/categoryBrowseUtils";
import { useWatchlist } from "features/watchlist";
import styles from "./AssetCard.module.css";

export type AssetCardProps = {
  asset: Asset;
  /** First segment of tags (e.g. genre); runs same filter as typing this text in search */
  onPrimaryTagSearch?: (label: string) => void;
};

const ClockIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 7V12L15 14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeWidth="2"
      opacity="0.9"
    />
  </svg>
);

const BookmarkOutline: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BookmarkFilled: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path
      d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function AssetCard({
  asset,
  onPrimaryTagSearch,
}: AssetCardProps) {
  const navigateToTitle = useNavigateToTitle();
  const watchlist = useWatchlist();
  const { primary, rest } = parsePrimaryTag(asset.tags);
  const tagSearchable = Boolean(onPrimaryTagSearch && primary.trim());
  const saved = watchlist.enabled && watchlist.isInWatchlist(asset.id);

  const handleCardActivate = () => {
    navigateToTitle(asset.id);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardActivate();
    }
  };

  const handleTagClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tagSearchable) onPrimaryTagSearch?.(primary.trim());
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      if (tagSearchable) onPrimaryTagSearch?.(primary.trim());
    }
  };

  const handleWatchlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    void watchlist.toggleWatchlist(asset);
  };

  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={handleCardActivate}
      onKeyDown={handleCardKeyDown}
      aria-label={`Open ${asset.title}`}
    >
      <div className={styles.imageWrap}>
        <img className={styles.image} src={asset.thumbnailUrl} alt={asset.title} />

        <div className={styles.badgeRow}>
          <div className={styles.badgePrice}>{asset.price}</div>
          <div className={styles.badgeDuration}>
            <ClockIcon />
            <span>{asset.duration}</span>
          </div>
        </div>

        {watchlist.enabled ? (
          <button
            type="button"
            className={[
              styles.watchlistFab,
              saved ? styles.watchlistFabActive : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={handleWatchlistClick}
            aria-label={
              saved ? `Remove ${asset.title} from watchlist` : `Add ${asset.title} to watchlist`
            }
            title={saved ? "Remove from watchlist" : "Add to watchlist"}
          >
            {saved ? <BookmarkFilled /> : <BookmarkOutline />}
          </button>
        ) : null}
      </div>

      <div className={styles.content}>
        <div className={styles.tags}>
          {tagSearchable ? (
            <>
              <button
                type="button"
                className={styles.tagLink}
                onClick={handleTagClick}
                onKeyDown={handleTagKeyDown}
                aria-label={`Search for ${primary.trim()}`}
              >
                {primary.trim()}
              </button>
              {rest ? (
                <span className={styles.tagsRest}>
                  {" "}
                  • {rest}
                </span>
              ) : null}
            </>
          ) : (
            <span className={styles.tagsPlain}>{asset.tags}</span>
          )}
        </div>
        <div className={styles.title}>{asset.title}</div>
        <div className={styles.description}>{asset.description}</div>
      </div>
    </div>
  );
}
